import React, { createContext, useContext, useState, useEffect } from "react";
import { authService } from "../services/authService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is already logged in on app start
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          const userInfo = await authService.getCurrentUser();
          setUser(userInfo);
          setIsAuthenticated(true);
        }
      } catch (error) {
        // Clear invalid tokens
        localStorage.removeItem("access_token");
        localStorage.removeItem("id_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("chatwoot_identifier_hash");
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async () => {
    try {
      // Suppress errors during login redirect
      const originalOnError = window.onerror;
      window.onerror = () => true;

      try {
        // Get authorization URL from backend
        const response = await authService.initiateLogin();
        // Redirect to OIDC provider
        // Keep errors suppressed during redirect
        window.location.href = response.authorization_url;
      } catch (loginError) {
        // Restore error handler before throwing
        window.onerror = originalOnError;
        throw loginError;
      }
      // Don't restore error handler if redirect succeeds - we're leaving the page
    } catch (error) {
      throw error;
    }
  };

  const handleCallback = async (code, state) => {
    try {
      setIsLoading(true);

      const response = await authService.handleCallback(code, state);

      // Store tokens
      localStorage.setItem("access_token", response.access_token);
      if (response.id_token) {
        localStorage.setItem("id_token", response.id_token);
      }
      if (response.refresh_token) {
        localStorage.setItem("refresh_token", response.refresh_token);
      }

      // CRITICAL: Chatwoot identifier hash is required for chat-ui
      // If not provided, authentication failed
      if (!response.chatwoot_identifier_hash) {
        throw new Error(
          "Falha na autenticação do chat: Não foi recebido o identificador necessário. Por favor contacte o seu administrador para configurar o chat interno."
        );
      }

      // Store Chatwoot identifier hash
      localStorage.setItem("chatwoot_identifier_hash", response.chatwoot_identifier_hash);

      // Use user info from callback response (no extra API call needed)
      setUser(response.user_info);
      setIsAuthenticated(true);

      return response;
    } catch (error) {
      // Always clear any partially stored tokens on failure
      localStorage.removeItem("access_token");
      localStorage.removeItem("id_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("chatwoot_identifier_hash");
      setUser(null);
      setIsAuthenticated(false);

      // Check for specific error types and provide helpful messages
      if (
        error.response?.status === 403 &&
        error.response?.data?.detail?.includes("role")
      ) {
        throw new Error(
          "Acesso negado: Não tem as permissões necessárias. Por favor contacte o seu administrador para lhe atribuir a função adequada."
        );
      } else if (error.response?.status === 500) {
        throw new Error(
          "Erro no servidor: Ocorreu um problema com o servidor de autenticação. Por favor tente novamente em breve."
        );
      } else if (error.response?.status === 400) {
        throw new Error(
          "Erro de autenticação: Pedido inválido. Por favor tente iniciar sessão novamente a partir da página principal."
        );
      } else if (error.code === "ERR_NETWORK") {
        throw new Error(
          "Erro de rede: Não foi possível ligar ao servidor de autenticação. Por favor verifique a sua ligação à internet."
        );
      }

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoggingOut(true);
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          const response = await authService.logout();

          // Clear local state and tokens first
          localStorage.removeItem("access_token");
          localStorage.removeItem("id_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("chatwoot_identifier_hash");
          setUser(null);
          setIsAuthenticated(false);

          // Redirect to Zitadel logout page if URL is provided
          if (response?.logout_url) {
            // Small delay to ensure state is cleared before redirect
            setTimeout(() => {
              window.location.href = response.logout_url;
            }, 100);
          } else {
            // No logout URL, just redirect to home
            setTimeout(() => {
              window.location.href = "/";
            }, 100);
          }
        } catch (logoutError) {
          // Even if logout API call fails, clear local state
          localStorage.removeItem("access_token");
          localStorage.removeItem("id_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("chatwoot_identifier_hash");
          setUser(null);
          setIsAuthenticated(false);

          // Redirect to home page
          setTimeout(() => {
            window.location.href = "/";
          }, 100);
        }
      } else {
        // No token, just clear local state
        localStorage.removeItem("access_token");
        localStorage.removeItem("id_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("chatwoot_identifier_hash");
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Even if logout fails, clear local state
      try {
        localStorage.removeItem("access_token");
        localStorage.removeItem("id_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("chatwoot_identifier_hash");
        setUser(null);
        setIsAuthenticated(false);
      } catch (e) {
        // Ignore errors during cleanup
      }
    } finally {
      // Don't set isLoggingOut to false if we're redirecting
      // The redirect will happen before this, so it doesn't matter
    }
  };

  const getAccessToken = () => {
    return localStorage.getItem("access_token");
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  const isAdmin = () => {
    return hasRole("admin");
  };

  const isUser = () => {
    return hasRole("user");
  };

  const isSuperAdmin = () => {
    return hasRole("super_admin");
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isLoggingOut,
    login,
    logout,
    handleCallback,
    getAccessToken,
    hasRole,
    isAdmin,
    isUser,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
